
import React, { useEffect, lazy, Suspense } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from "react-router-dom";
import App from './App.tsx';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/auth"; // Using the simplified auth provider
import { useStore } from "@/store/useStore";
import { dealersCache } from "@/hooks/dealers/useDealersCache";
import { initGeolocation } from "@/lib/geolocation/init";
import { prefetchRecommendedDealers } from "@/hooks/recommended-dealers/prefetch";

// Importazione esplicita dei CSS
import './index.css';

// Detect if we're on mobile
const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

// Componente per il precaricamento dei dati con performance ottimizzata
const DataPreloader = () => {
  const { fetchCurrentPricesData, fetchPriceHistoryData, selectedTimePeriod } = useStore();
  
  // Check if we're on the charts page or compro-oro page
  const isChartsPage = typeof window !== 'undefined' && 
    (window.location.pathname === '/grafici' || window.location.pathname.includes('/compro-oro'));
  
  useEffect(() => {
    // Flag per tracciare l'esecuzione
    let prefetchStarted = false;
    
    // Funzione per precaricare tutti i dati necessari - ottimizzata per mobile
    const preloadData = async () => {
      if (prefetchStarted) return;
      prefetchStarted = true;
      
      console.log("Inizializzazione precaricamento dati...");
      
      try {
        // 1. Caricare i prezzi correnti (priorità alta)
        console.log("Caricamento prezzi correnti...");
        await fetchCurrentPricesData();
        
        // 2. Se siamo nella pagina dei grafici, caricare anche i dati storici
        if (isChartsPage) {
          console.log("Pagina grafici rilevata, caricamento dati storici...");
          try {
            const result = await fetchPriceHistoryData(selectedTimePeriod);
            console.log("Dati storici caricati con successo:", {
              gold: result.goldPriceHistory.length,
              silver: result.silverPriceHistory.length
            });
            
            // Dispatch a custom event to notify components that data is loaded
            window.dispatchEvent(new CustomEvent('chartDataLoaded'));
          } catch (error) {
            console.error("Errore durante il caricamento dei dati storici:", error);
          }
        }
        
        // 3. Caricare immediatamente i compro oro consigliati (nuova priorità alta)
        console.log("Precaricamento compro oro consigliati...");
        await prefetchRecommendedDealers();
        
        // Per i dispositivi mobili, riduciamo il numero di operazioni in background
        if (isMobile) {
          return;
        }
        
        // 4. Inizializzare la geolocalizzazione in background
        console.log("Inizializzazione geolocalizzazione...");
        initGeolocation().catch(e => 
          console.error("Errore inizializzazione geolocalizzazione:", e)
        );
        
        console.log("Inizializzazione precaricamento dati completata");
      } catch (error) {
        console.error("Errore durante il precaricamento dei dati:", error);
        prefetchStarted = false;
      }
    };
    
    // Avvia il precaricamento
    preloadData();
    
  }, [fetchCurrentPricesData, fetchPriceHistoryData, selectedTimePeriod, isChartsPage]);
  
  return null; // Questo componente non renderizza nulla
};

const container = document.getElementById('root');

if (!container) {
  throw new Error('Failed to find the root element');
}

// Create a new QueryClient instance with options suitable for hydration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
      // Disable queries during hydration to avoid issues
      enabled: false,
    },
  },
});

// Create Helmet context to use throughout the app
const helmetContext = {};

// Check if container has content (SSR mode)
const containerHasContent = container.innerHTML.trim() !== '' && 
                           container.hasAttribute('data-hydrate');

// App JSX with all necessary providers for correct hydration
const AppWithProviders = () => {
  // Check if we have server-rendered data to hydrate from
  const hasServerStore = typeof window !== 'undefined' && 
                         window.__SERVER_RENDER_TIME && 
                         containerHasContent;
  
  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider context={helmetContext}>
          <BrowserRouter>
            <ThemeProvider defaultTheme="light" defaultMetal="gold" ssrMode={containerHasContent}>
              <TooltipProvider>
                <AuthProvider>
                  {/* Run DataPreloader if not hydrating from SSR or if on charts/compro-oro page */}
                  {(!containerHasContent || isChartsPage || window.location.pathname.includes('/compro-oro')) && (
                    <Suspense fallback={null}>
                      <DataPreloader />
                    </Suspense>
                  )}
                  <App />
                </AuthProvider>
              </TooltipProvider>
            </ThemeProvider>
          </BrowserRouter>
        </HelmetProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
};

// Add a type declaration for the server render time and data
declare global {
  interface Window {
    __SERVER_RENDER_TIME?: string;
    __INITIAL_DEALER_DATA__?: Record<string, any>;
    __PREDICTIONS_DATA?: {
      predictions: any[];
    };
    __SERVER_STORE?: {
      metalPrices?: any;
      dealerData?: Record<string, any>;
    };
  }
}

// Initialize dealer data from server if available
if (typeof window !== 'undefined' && window.__SERVER_STORE?.dealerData) {
  console.log("Initializing dealer data from server");
  window.__INITIAL_DEALER_DATA__ = window.__SERVER_STORE.dealerData;
}

// Extract predictions data from script tag if available
if (typeof window !== 'undefined') {
  const predictionsDataScript = document.getElementById('__PREDICTIONS_DATA__');
  if (predictionsDataScript) {
    try {
      const predictionsData = JSON.parse(predictionsDataScript.textContent || '{}');
      window.__PREDICTIONS_DATA = predictionsData;
      console.log(`Initialized predictions data from server: ${predictionsData.predictions?.length || 0} predictions`);
    } catch (error) {
      console.error('Error parsing predictions data:', error);
    }
  }
}

// Check if we're on the charts page or compro-oro page
const isChartsPage = typeof window !== 'undefined' && 
  (window.location.pathname === '/grafici' || window.location.pathname.includes('/compro-oro'));

// For hydration, use createRoot for empty containers (CSR) and hydrateRoot for non-empty containers (SSR)
if (containerHasContent) {
  // If container is not empty, we're in SSR mode - hydrate
  console.log("Hydrating from server-rendered content");
  console.log(`Server render time: ${window.__SERVER_RENDER_TIME || 'unknown'}`);
  
  // Perform hydration with exact server-rendered content
  hydrateRoot(container, <AppWithProviders />);
  
  // For charts page, enable queries immediately to ensure data loading
  if (isChartsPage) {
    console.log("Charts page detected, enabling queries immediately");
    queryClient.setDefaultOptions({
      queries: {
        ...queryClient.getDefaultOptions().queries,
        enabled: true,
      },
    });
  } else {
    // For other pages, re-enable queries after hydration is complete with a delay
    setTimeout(() => {
      console.log("Hydration complete, enabling client-side queries for updates");
      queryClient.setDefaultOptions({
        queries: {
          ...queryClient.getDefaultOptions().queries,
          enabled: true,
        },
      });
      
      // After hydration, we can prefetch additional data for future navigation
      // but we don't need to re-fetch what was already rendered on the server
      console.log("Prefetching additional data for future navigation");
    }, 1000); // Longer delay to ensure hydration is fully complete
  }
} else {
  // If container is empty, we're in CSR mode - create a new root
  console.log("Creating new root (CSR mode)");
  
  // For charts page in CSR mode, enable queries immediately
  if (isChartsPage) {
    console.log("Charts page in CSR mode, enabling queries immediately");
    queryClient.setDefaultOptions({
      queries: {
        ...queryClient.getDefaultOptions().queries,
        enabled: true,
      },
    });
  }
  
  createRoot(container).render(<AppWithProviders />);
}
